import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type {
  CreateDashboardPayload,
  Dashboard,
  UpdateDashboardPayload,
} from "@web-app/types";
import http from "@web-app/utils/http";

// Query keys
export const dashboardKeys = {
  all: ["dashboards"] as const,
  detail: (id: string) => ["dashboards", id] as const,
  lists: () => [...dashboardKeys.all, "list"] as const,
};

// Fetch all dashboards
export function useDashboards() {
  return useQuery({
    queryFn: async () => {
      const response = await http.get<Dashboard[]>("/dashboards");
      return response.data;
    },
    queryKey: dashboardKeys.lists(),
  });
}

// Fetch single dashboard
export function useDashboard(id: string) {
  return useQuery({
    enabled: !!id,
    queryFn: async () => {
      const response = await http.get<Dashboard>(`/dashboards/${id}`);
      return response.data;
    },
    queryKey: dashboardKeys.detail(id),
  });
}

// Create dashboard mutation
export function useCreateDashboard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateDashboardPayload) => {
      const response = await http.post<Dashboard>("/dashboards", payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dashboardKeys.lists() });
    },
  });
}

// Update dashboard mutation
export function useUpdateDashboard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdateDashboardPayload;
    }) => {
      const response = await http.put<Dashboard>(`/dashboards/${id}`, payload);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: dashboardKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: dashboardKeys.detail(data.id),
      });
    },
  });
}

// Duplicate dashboard mutation: creates a copy of a dashboard, including all
// of its charts, in a single backend request so the duplication is atomic.
export function useDuplicateDashboard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (dashboardId: string) => {
      const response = await http.post<Dashboard>(
        `/dashboards/${dashboardId}/duplicate`,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dashboardKeys.lists() });
    },
  });
}

// Delete dashboard mutation
export function useDeleteDashboard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await http.delete(`/dashboards/${id}`);
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dashboardKeys.lists() });
    },
  });
}
